import { useState, useRef, useEffect, useCallback } from "react";
import { Box, Button, Checkbox, CircularProgress, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, FormGroup, InputAdornment, Radio, Stack, Switch, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import HelpIcon from "@mui/icons-material/Help";
import { useQuery } from "react-query";
import axiosClient from "../../lib/axios";
import BtnCarregarDados from "../BtnCarregarDados";


export default function DialogContentSelecionarLoja({
    defaultValue,
    extraParams = {},
    onChange,
    handleClose,
    multiple = false,
    autoSelecionarPrimeiro = false,
}: DialogContentSelecionarLojaProps) {

    const [limit, setLimit] = useState(20);
    const [search, setSearch] = useState("");
    const [somenteAtivas, setSomenteAtivas] = useState(true);
    const [somenteEstoque, setSomenteEstoque] = useState(false);

    const [selecionados, setSelecionados] = useState<TLoja[]>(() => {
        if (multiple) {
            const defaultArray = Array.isArray(defaultValue) ? defaultValue : [];
            return defaultArray.filter(item => item.id !== 0);
        } else {
            const singleValue = defaultValue as TLoja;
            return singleValue.id !== 0 ? [singleValue] : [];
        }
    });

    const inputRef = useRef<HTMLInputElement>(null);

    const { data, isFetching, isLoading } = useQuery({
        queryKey: ["pesquisar-lojas", search, limit, somenteAtivas, somenteEstoque],
        queryFn: () =>
            axiosClient.get("/pesquisar-lojas", {
                params: {
                    limit,
                    page: 1,
                    ...extraParams,
                    pesquisar: search,
                    somente_ativas: somenteAtivas,
                    somente_estoque: somenteEstoque,
                },
            }),
        cacheTime: 0,
        staleTime: 0,
        onSuccess: (data) => {
            if (inputRef.current) {
                inputRef.current.focus();
            }

            // Auto-selecionar o primeiro item se a prop estiver ativa e não houver seleção
            if (autoSelecionarPrimeiro && data?.data?.data?.length > 0 && selecionados.length === 0) {
                const primeiroItem = data.data.data[0];
                if (primeiroItem.id !== 0) {
                    setSelecionados([primeiroItem]);
                }
            }
        },
    });

    const lojas = data?.data?.data || [];
    const total = data?.data?.total || 0;

    const handleClick = useCallback(
        (item: TLoja) => {
            if (multiple) {
                const index = selecionados.findIndex((i) => i.id === item.id);
                if (index === -1) {
                    setSelecionados((prevState) => [...prevState, item]);
                } else {
                    setSelecionados((prevState) =>
                        prevState.filter((_, i) => i !== index)
                    );
                }
            }
            if (!multiple) setSelecionados([item]);
        },
        [selecionados, multiple]
    );

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const handleSubmit = () => {
        if (!multiple) {
            onChange(selecionados[0]);
            return;
        }
        onChange(selecionados);
    };

    return (
        <>
            <DialogTitle>Selecionar loja</DialogTitle>
            <DialogContent sx={{ mt: { xs: 2, md: 0 } }}>
                <Box display="flex" alignItems="center" gap={2} py={2}>
                    <TextField
                        value={search}
                        fullWidth
                        inputRef={inputRef}
                        label="Pesquisar"
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }
                        }}
                    onChange={(e) => setSearch(e.target.value)}
                    />
                </Box>

                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={somenteAtivas}
                                onChange={() => setSomenteAtivas((prev) => !prev)}
                            />
                        }
                        label="Somente Lojas Ativas"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={somenteEstoque}
                                onChange={() => setSomenteEstoque((prev) => !prev)}
                            />
                        }
                        label="Somente Lojas de Estoque"
                    />
                </Stack>

                <Divider sx={{ mb: 2 }} />

                {isLoading && (
                    <Box display="flex" justifyContent="center" my={2}>
                        <CircularProgress />
                    </Box>
                )}

                {!isLoading && (
                    <FormGroup>
                        <Box
                            display="flex"
                            overflow="auto"
                            height={{ xs: 550, md: 350 }}
                            flexDirection="column"
                        >
                            {lojas && lojas.length <= 0 && (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    paddingY={2}
                                    gap={1}
                                    color="gray"
                                >
                                    <HelpIcon />
                                    <Typography>Nenhum resultado encontrado</Typography>
                                </Box>
                            )}

                            {lojas &&
                                lojas.length > 0 &&
                                lojas.map((i: TLoja, index: number) => {
                                    const isChecked = selecionados.some(
                                        (item) => item.id === i.id
                                    );
                                    return (
                                        <FormControlLabel
                                            key={index}
                                            onChange={() => handleClick(i)}
                                            control={
                                                multiple ? (
                                                    <Checkbox checked={isChecked} />
                                                ) : (
                                                    <Radio checked={isChecked} />
                                                )
                                            }
                                            label={
                                                <Box>
                                                    <Typography>
                                                        {`${i.id} - ${i.nome}`}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    );
                                })}
                            <BtnCarregarDados
                                {...{ limit, isFetching }}
                                total={total}
                                action={() => setLimit((prevState) => prevState + 20)}
                            />
                        </Box>
                    </FormGroup>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancelar</Button>
                <Button
                    autoFocus
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={
                        selecionados?.[0]?.id === 0
                    }
                >
                    Selecionar
                </Button>
            </DialogActions>
        </>
    );
}


interface DialogContentSelecionarLojaProps {
    defaultValue: TLoja | TLoja[];
    extraParams?: { [key: string]: any };
    handleClose: () => void;
    onChange: (arg: TLoja | TLoja[]) => void;
    multiple?: boolean;
    autoSelecionarPrimeiro?: boolean;
}

export type TLoja = {
    id: number;
    nome: string;
    razao_social?: string;
    cidade?: string;
    uf?: string;
}
